// Распределения.
//
// ФОРМА ГРАФИКА ВЫБИРАЕТСЯ ПО ДАННЫМ, А НЕ ПО ПРИВЫЧКЕ. На тысяче
// прогонов из десяти показываемых величин широкое распределение имеет
// ровно одна — площадь пола (81 различное значение). У остальных от трёх
// до восьми. Ящик с усами для величины на шесть значений рисует хвост,
// которого нет, а гистограмма с корзинами либо слипается, либо разъезжается.
// Поэтому тесные величины показаны ЧАСТОТНОЙ ТАБЛИЦЕЙ: одно значение —
// одна строка, «врагов 11 — два прогона из тысячи». Ящик и настоящая
// гистограмма остаются широким величинам, где они уместны.

import { useMemo, useState } from 'react';
import { useStore } from '../data/store';
import { Callout, OriginTag, Panel } from '../components/Ui';
import { BoxPlots, Histogram } from '../components/Charts';
import {
  METRICS,
  TIGHT_DISTINCT_MAX,
  describe,
  distinctCount,
  frequencies,
  histogram,
  isTight,
  type FrequencyRow,
  type Metric,
  type Percentiles,
} from '../lib/stats';
import { num, percent } from '../lib/format';

export function Distributions() {
  const store = useStore();
  const { active, summary } = store;
  const [split, setSplit] = useState<'none' | 'deficit' | 'fallback'>('none');

  const columns = useMemo(() => {
    return METRICS.map((metric) => {
      const values = active.map(metric.pick);
      return {
        metric,
        values,
        stats: describe(values),
        distinct: distinctCount(values),
        tight: isTight(values),
      };
    });
  }, [active]);

  const wide = useMemo(() => columns.filter((c) => !c.tight), [columns]);
  const tight = useMemo(() => columns.filter((c) => c.tight), [columns]);

  const groups = useMemo(() => {
    if (split === 'none') return null;
    const pick =
      split === 'deficit'
        ? (row: (typeof active)[number]) => row.economy.respectsAmmoDeficit
        : (row: (typeof active)[number]) => !row.layout.usedFallback;
    return { a: active.filter(pick), b: active.filter((row) => !pick(row)) };
  }, [active, split]);

  // Столбики и коробки считаются ЗДЕСЬ, а не в разметке.
  //
  // Массив, собранный прямо в JSX, — новый объект на каждую перерисовку,
  // а по нему график пересобирается целиком, вместе с элементом, на
  // котором держится выделение области. Внешне это выглядит как «зум не
  // работает», и найти такое стоит вечера.
  const boxRows = useMemo(
    () => wide.map((c) => ({ label: c.metric.label, values: c.values })),
    [wide],
  );
  const histograms = useMemo(
    () =>
      wide.map((column) => ({
        column,
        bins: histogram(groups ? groups.a.map(column.metric.pick) : column.values),
        overlay:
          groups && groups.b.length > 0 ? histogram(groups.b.map(column.metric.pick)) : null,
      })),
    [wide, groups],
  );

  if (active.length === 0) {
    return (
      <div className="page">
        <div className="empty">выборка пуста</div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>Распределения</h1>
        <p>
          {num(active.length)} прогонов, отпечаток{' '}
          <span className="mono">{summary.configHash ?? 'смешанный'}</span>. Форму показа выбирают
          сами данные: величины с числом различных значений до {TIGHT_DISTINCT_MAX} идут частотной
          таблицей, остальные — гистограммой и ящиком с усами.
        </p>
        <OriginTag origin={store.runsOrigin} />
        <label className="field" style={{ marginLeft: 'auto' }}>
          разрез
          <select value={split} onChange={(e) => setSplit(e.target.value as typeof split)}>
            <option value="none">без разреза</option>
            <option value="deficit">дефицит соблюдён / сломан</option>
            <option value="fallback">обычная / запасная планировка</option>
          </select>
        </label>
      </div>

      <Callout tone="info" title="Почему здесь почти нет гистограмм">
        <p>
          Из {num(columns.length)} величин широкое распределение имеет{' '}
          <b>{num(wide.length)}</b>:{' '}
          <span className="mono">{wide.map((c) => c.metric.label).join(', ') || '—'}</span>. У
          остальных различных значений от {num(Math.min(...tight.map((c) => c.distinct)))} до{' '}
          {num(Math.max(...tight.map((c) => c.distinct)))} — столько, что усы нарисовали бы хвост,
          которого нет. Для них правдивее частотная таблица: видно, что уровень почти всегда даёт
          шесть врагов, а одиннадцать бывают дважды на тысячу.
        </p>
      </Callout>

      {groups && (
        <Callout tone="info" title="Разрез включён">
          <p>
            Первая группа — {split === 'deficit' ? 'дефицит соблюдён' : 'обычная планировка'} (
            {num(groups.a.length)} прогонов), вторая —{' '}
            {split === 'deficit' ? 'избыток патронов' : 'запасная планировка'} ({num(groups.b.length)}).
            Если вторая группа пуста, второго ряда не будет — таких прогонов в выборке нет.
          </p>
        </Callout>
      )}

      {wide.length > 0 && (
        <>
          <Panel
            title="Широкие величины: ящик с усами"
            aside={
              <span className="dim">
                коробка p25…p75, риска — медиана, усы 1.5×IQR, точки за ними — выбросы
              </span>
            }
          >
            <BoxPlots rows={boxRows} />
          </Panel>

          <div className="charts">
            {histograms.map(({ column, bins, overlay }) => (
              <div className="chart" key={column.metric.key}>
                <h3>{column.metric.label}</h3>
                <div className="sub">
                  {column.metric.unit} · различных значений {num(column.distinct)} · MAD{' '}
                  {num(column.stats.mad, 2)}
                </div>
                <Histogram
                  bins={bins}
                  overlay={overlay}
                  stats={column.stats}
                  unit={column.metric.unit}
                />
              </div>
            ))}
          </div>
        </>
      )}

      {tight.map((column) => (
        <FrequencyPanel
          key={column.metric.key}
          metric={column.metric}
          stats={column.stats}
          rows={frequencies(column.values)}
          total={column.values.length}
          overlay={
            groups && groups.b.length > 0
              ? frequencies(groups.b.map(column.metric.pick))
              : null
          }
          overlayTotal={groups?.b.length ?? 0}
        />
      ))}

      <Panel title="Доли, которые не выводятся из перцентилей">
        <table>
          <thead>
            <tr>
              <th>Признак</th>
              <th className="n">прогонов</th>
              <th className="n">доля</th>
              <th>Что это значит</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Дефицит патронов сломан</td>
              <td className="n">{num(Math.round(summary.ammoSurplusShare * summary.rows))}</td>
              <td className={summary.ammoSurplusShare > 0 ? 'n bad' : 'n good'}>
                {percent(summary.ammoSurplusShare)}
              </td>
              <td className="dim">патронов на уровне больше, чем нужно жадному профилю</td>
            </tr>
            <tr>
              <td>Планировка собрана запасным путём</td>
              <td className="n">{num(Math.round(summary.layoutFallbackShare * summary.rows))}</td>
              <td className={summary.layoutFallbackShare > 0 ? 'n warn' : 'n good'}>
                {percent(summary.layoutFallbackShare)}
              </td>
              <td className="dim">генератор не уложился в обычные правила</td>
            </tr>
            <tr>
              <td>Уровень без врагов</td>
              <td className="n">{num(Math.round(summary.noEnemiesShare * summary.rows))}</td>
              <td className={summary.noEnemiesShare > 0 ? 'n bad' : 'n good'}>
                {percent(summary.noEnemiesShare)}
              </td>
              <td className="dim">спуск без единого противника</td>
            </tr>
            <tr>
              <td>Отступ от входа ослаблен</td>
              <td className="n">{num(Math.round(summary.relaxedClearanceShare * summary.rows))}</td>
              <td className={summary.relaxedClearanceShare > 0 ? 'n warn' : 'n good'}>
                {percent(summary.relaxedClearanceShare)}
              </td>
              <td className="dim">врага пришлось поставить ближе к входу, чем задумано</td>
            </tr>
            <tr>
              <td>Комнаты налезли друг на друга</td>
              <td className="n">{num(Math.round(summary.cellCollisionShare * summary.rows))}</td>
              <td className={summary.cellCollisionShare > 0 ? 'n bad' : 'n good'}>
                {percent(summary.cellCollisionShare)}
              </td>
              <td className="dim">нарушен инвариант раскладки</td>
            </tr>
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

/** Частотная таблица одной тесной величины: одно значение — одна строка. */
function FrequencyPanel({
  metric,
  stats,
  rows,
  total,
  overlay,
  overlayTotal,
}: {
  metric: Metric;
  stats: Percentiles;
  rows: FrequencyRow[];
  total: number;
  overlay: FrequencyRow[] | null;
  overlayTotal: number;
}) {
  const maxCount = Math.max(1, ...rows.map((r) => r.count));
  const overlayByValue = new Map((overlay ?? []).map((r) => [r.value, r]));

  return (
    <Panel
      title={`${metric.label} — ${num(rows.length)} различных значений`}
      flush
      aside={
        <span className="dim mono">
          p10 {num(stats.p10)} · p25 {num(stats.p25)} · p50 {num(stats.p50)} · p75 {num(stats.p75)} ·
          p90 {num(stats.p90)} · среднее {num(stats.mean, 2)}
        </span>
      }
    >
      <table>
        <thead>
          <tr>
            <th className="n">значение</th>
            <th className="n">прогонов</th>
            <th className="n">доля</th>
            <th style={{ width: '38%' }}>частота</th>
            <th className="n">накопленная</th>
            {overlay && <th className="n">вторая группа</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isMedian = row.value === stats.p50;
            const rare = row.share < 0.01;
            const second = overlayByValue.get(row.value);
            return (
              <tr key={row.value}>
                <td className={isMedian ? 'n mono good' : 'n mono'}>
                  {num(row.value)}
                  {isMedian && <span className="faint"> ← медиана</span>}
                </td>
                <td className="n">{num(row.count)}</td>
                <td className={rare ? 'n warn' : 'n'}>{percent(row.share, 1)}</td>
                <td>
                  <div style={{ background: 'var(--line-soft)', height: 9, borderRadius: 1 }}>
                    <div
                      style={{
                        background: rare ? 'var(--amber)' : 'var(--phosphor)',
                        width: `${(row.count / maxCount) * 100}%`,
                        height: '100%',
                        opacity: 0.75,
                      }}
                    />
                  </div>
                </td>
                <td className="n faint">{percent(row.cumulative, 1)}</td>
                {overlay && (
                  <td className="n dim">
                    {second ? `${num(second.count)} · ${percent(second.count / Math.max(1, overlayTotal), 1)}` : '—'}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="dim" style={{ margin: '8px 14px 12px' }}>
        Всего {num(total)} прогонов. Жёлтым отмечены значения, встретившиеся меньше чем в 1%
        прогонов, — именно они попадают в «хвосты частотных таблиц» на странице «Аномалии».
      </p>
    </Panel>
  );
}
